/**
 * Offline Manager
 * Handles offline detection, transaction queuing, and synchronization
 */

import { v4 as uuidv4 } from 'uuid';
import db from './db';
import axios from 'axios';
import syncEngine from './syncEngine';

class OfflineManager {
  constructor() {
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.listeners = new Set();
    this.maxRetries = 3;
    this.retryDelay = 2000; // 2 seconds
    this.backgroundSyncInterval = null;
    this.backgroundSyncIntervalMs = 30000; // Sync every 30 seconds when online (less frequent to reduce load)
    
    // Priority levels for transactions
    this.PRIORITY = {
      CRITICAL: 1,  // Orders, Payments
      HIGH: 2,      // Inventory updates
      MEDIUM: 3,    // Customer data
      LOW: 4        // Reports, Logs
    };

    // Initialize online/offline listeners
    this.initializeListeners();
    
    // Clean up any stale auth transactions on startup
    this.cleanupStaleTransactions();
    
    // Start background sync if online
    if (this.isOnline) {
      this.startBackgroundSync();
    }
  }

  /**
   * Clean up stale auth transactions on startup
   */
  async cleanupStaleTransactions() {
    try {
      const clearedCount = await db.clearStaleAuthTransactions();
      if (clearedCount > 0) {
        console.log(`🧹 Startup cleanup: Removed ${clearedCount} stale auth transactions`);
      }
    } catch (error) {
      console.error('Error during startup cleanup:', error);
    }
  }

  /**
   * Initialize network status listeners
   */
  initializeListeners() {
    window.addEventListener('online', async () => {
      console.log('🟢 Network: ONLINE');
      this.isOnline = true;
      this.notifyListeners({ online: true });
      
      // Start background sync
      this.startBackgroundSync();
      
      // Check if there are pending transactions before syncing
      const pendingCount = await db.getPendingCount();
      console.log(`📊 Found ${pendingCount} pending transactions`);
      
      if (pendingCount === 0) {
        console.log('✅ No pending transactions to sync');
        return;
      }
      
      // Auto-sync pending transactions
      const syncResult = await this.syncPendingTransactions();

      // Notify listeners so UI can refresh selectively instead of full reload
      if (syncResult && pendingCount > 0) {
        console.log('✅ Auto-sync successful, notifying listeners to refresh data');
        this.notifyListeners({ autoSyncComplete: true });
      } else if (!syncResult) {
        console.log('⚠️ Auto-sync failed, manual sync required');
        this.notifyListeners({ online: true, syncFailed: true });
      }
    });

    window.addEventListener('offline', () => {
      console.log('🔴 Network: OFFLINE');
      this.isOnline = false;
      this.stopBackgroundSync();
      this.notifyListeners({ online: false });
    });
  }

  /**
   * Start background sync interval
   * Syncs pending transactions every X seconds when online
   * ONLY syncs if server is actually reachable (not just navigator.onLine)
   */
  startBackgroundSync() {
    if (this.backgroundSyncInterval) return; // Already running
    
    console.log(`⏰ Starting background sync (every ${this.backgroundSyncIntervalMs/1000}s)`);
    this.backgroundSyncInterval = setInterval(async () => {
      if (this.isOnline && !this.syncInProgress) {
        const pendingCount = await db.getPendingCount();
        if (pendingCount > 0) {
          // First check if server is actually reachable before syncing
          const serverReachable = await this.checkServerReachable();
          if (serverReachable) {
            console.log(`🔄 Background sync: ${pendingCount} pending transactions`);
            await this.syncPendingTransactions(true); // true = silent mode (no UI refresh)
          } else {
            console.log(`📴 Background sync skipped: Server not reachable`);
          }
        }
      }
    }, this.backgroundSyncIntervalMs);
  }

  /**
   * Check if server is actually reachable (quick ping)
   * Uses HEAD request to minimize data transfer
   */
  async checkServerReachable() {
    try {
      const baseUrl = axios.defaults.baseURL || localStorage.getItem('baseUrl') || 'http://localhost:3100';
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      // Try a simple HEAD request to the base URL
      // Most servers respond to this even without a specific health endpoint
      const response = await fetch(baseUrl, {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-store'
      }).catch(() => null);
      
      clearTimeout(timeoutId);
      
      // Server is reachable if we get any response (even 404)
      // Only network errors return null
      return response !== null;
    } catch (e) {
      return false;
    }
  }

  /**
   * Stop background sync
   */
  stopBackgroundSync() {
    if (this.backgroundSyncInterval) {
      clearInterval(this.backgroundSyncInterval);
      this.backgroundSyncInterval = null;
      console.log('⏹️ Background sync stopped');
    }
  }

  /**
   * Check if actually online (ping server)
   * DISABLED - was causing false offline detections
   */
  async checkOnlineStatus() {
    // Just use navigator.onLine - it's more reliable
    this.isOnline = navigator.onLine;
  }

  /**
   * Subscribe to online/offline status changes
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners(data) {
    this.listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in listener callback:', error);
      }
    });
  }

  /**
   * Queue transaction for offline sync
   */
  async queueTransaction(config) {
    try {
      // Don't queue read operations - they should be served from cache, not synced
      if (this.isReadOperation(config.url)) {
        console.log(`📖 Skipping queue for read operation: ${config.url}`);
        return { transactionId: null, skipped: true };
      }
      
      const transactionId = uuidv4();
      const branchId = this.extractBranchId(config);
      const priority = this.determinePriority(config);

      // Extract orderId for dependency tracking (payment/cancel reference their order)
      let dependsOn = null;
      if (config.url?.includes('postPosPayment') || 
          config.url?.includes('cancelOngoingOrders')) {
        const requestData = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
        dependsOn = requestData?.orderId || requestData?.id;
        console.log(`📎 Payment/Cancel transaction depends on order: ${dependsOn}`);
        console.log(`   Request data orderId: ${requestData?.orderId}, id: ${requestData?.id}`);
      }

      const transaction = {
        transactionId,
        endpoint: config.url,
        method: config.method.toUpperCase(),
        data: config.data,
        headers: config.headers,
        params: config.params,
        priority,
        branchId,
        dependsOn,  // Link payment/cancel to their order
        metadata: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      };

      await db.addTransaction(transaction);
      
      console.log(`📥 Transaction queued: ${transactionId} (${config.method} ${config.url})`);
      
      this.notifyListeners({ 
        transactionQueued: true, 
        transactionId,
        pendingCount: await this.getPendingCount()
      });

      return { transactionId, queued: true };
    } catch (error) {
      console.error('Error queuing transaction:', error);
      throw error;
    }
  }

  /**
   * Determine transaction priority based on endpoint
   */
  determinePriority(config) {
    const url = config.url.toLowerCase();
    
    // Critical operations
    if (url.includes('order') || url.includes('payment') || url.includes('billing')) {
      return this.PRIORITY.CRITICAL;
    }
    
    // High priority
    if (url.includes('inventory') || url.includes('stock')) {
      return this.PRIORITY.HIGH;
    }
    
    // Medium priority
    if (url.includes('customer') || url.includes('vendor')) {
      return this.PRIORITY.MEDIUM;
    }
    
    // Low priority (default)
    return this.PRIORITY.LOW;
  }

  /**
   * Check if URL is a read operation or auth endpoint (should not be queued for sync)
   */
  isReadOperation(url) {
    if (!url) return false;
    const skipEndpoints = [
      // Read operations - should be served from cache
      'listCompletedSelfOrder',
      'listPosOrders',
      'stockreport',
      'viewReservationList',
      'viewReservation',
      'getProducts',
      'getCategories',
      'getBranchConfiguration',
      'listProducts',
      'listCategories',
      // Auth endpoints - should NOT be queued for offline sync
      'admin/login',
      'admin/checkdevice',
      'admin/logout',
      'admin/refreshToken',
      // Report endpoints - read-only, no need to sync
      'listTimeReport',
      'shiftReportSale',
      'shiftReportProducts',
      'shiftReportPaymentSummary',
      'shiftSummeryReport',
      'salesDetailsReport',
      'aggregatorReport',
      // Account report endpoints - read-only, must never be queued
      'accountReceivableAgingReport',
      'accountPayableAgingReport',
      'singleCustomerARAR',
      'accountReceivableReport',
      'accountPayableReport',
      'agingReport',
      'inOutVatReport',
      'viewExpenseReport',
      'viewlog',
      'getlog',
    ];
    return skipEndpoints.some(e => url.includes(e));
  }

  /**
   * Extract branch ID from request config
   */
  extractBranchId(config) {
    try {
      // Try to extract from data
      if (config.data?.branchId) return config.data.branchId;
      if (config.data?.branchPk) return config.data.branchPk;
      
      // Try to extract from params
      if (config.params?.branchId) return config.params.branchId;
      
      // Try to extract from localStorage
      const loginResponse = localStorage.getItem('loginResponse');
      if (loginResponse) {
        const parsed = JSON.parse(loginResponse);
        return parsed?.branchPk || null;
      }
      
      return null;
    } catch (error) {
      console.error('Error extracting branch ID:', error);
      return null;
    }
  }

  /**
   * Sync all pending transactions
   * NOW USES NEW SYNC ENGINE WITH DEPENDENCY RESOLUTION
   * @param {boolean} silentMode - If true, don't notify listeners (no UI refresh)
   */
  async syncPendingTransactions(silentMode = false) {
    if (this.syncInProgress) {
      console.log('⏳ Sync already in progress, skipping');
      return false;
    }
    
    if (!this.isOnline) {
      console.log('📴 Device offline, cannot sync');
      return false;
    }

    try {
      this.syncInProgress = true;
      console.log('🔄 Starting sync process via offlineManager...');
      
      // Delegate to new sync engine (pass silentMode to prevent UI refresh)
      const result = await syncEngine.syncAll(silentMode);
      
      // Only notify listeners if NOT in silent mode and there were actual syncs
      if (!silentMode && (result.successCount > 0 || result.failCount > 0)) {
        this.notifyListeners({
          syncComplete: true,
          successCount: result.successCount || 0,
          failCount: result.failCount || 0,
          allSuccess: result.success,
          pendingCount: result.pendingCount || 0
        });
      }
      
      console.log(`✅ Sync completed: ${result.successCount} success, ${result.failCount} failed`);
      return result.success;
    } catch (error) {
      console.error('❌ Error during sync:', error);
      return false;
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Sync individual transaction
   */
  async syncTransaction(transaction) {
    try {
      // Check if max retries exceeded
      if (transaction.retryCount >= this.maxRetries) {
        console.warn(`Max retries exceeded for transaction ${transaction.transactionId}`);
        await db.updateTransactionStatus(transaction.transactionId, 'failed');
        await db.logConflict({
          transactionId: transaction.transactionId,
          conflictType: 'max_retries_exceeded',
          data: transaction
        });
        return;
      }

      // Make the API request
      const response = await axios({
        method: transaction.method,
        url: transaction.endpoint,
        data: transaction.data,
        headers: transaction.headers,
        params: transaction.params,
        timeout: 30000 // 30 seconds
      });

      // Success - remove from queue
      await db.deleteTransaction(transaction.transactionId);
      console.log(`✅ Synced: ${transaction.transactionId}`);

      // Clean up offline order if this was an addPosOrder transaction
      if (transaction.endpoint.includes('addPosOrder')) {
        try {
          // Verify offline order exists before deletion
          const offlineOrder = await db.ordersCache.get(transaction.transactionId);
          if (offlineOrder) {
            console.log(`🔍 Found offline order to delete: ${transaction.transactionId}, ordName: ${offlineOrder.ordName}`);
            await db.ordersCache.delete(transaction.transactionId);
            console.log(`🗑️ Deleted offline order: ${transaction.transactionId}`);
          } else {
            console.warn(`⚠️ Offline order not found in cache: ${transaction.transactionId}`);
          }
        } catch (error) {
          console.error('❌ Error deleting offline order:', error);
        }
      }

      // Cache the response if it's an order
      if (transaction.endpoint.includes('order') && response.data) {
        const serverOrderId = response.data._id || response.data.id;
        console.log(`💾 Caching synced order: ${serverOrderId}`);
        await db.cacheOrder({
          orderId: serverOrderId,
          ...response.data,
          offlineOrder: false, // Mark as synced/online order
          syncStatus: 'synced',
          branchId: transaction.branchId
        });
      }

      return response;

    } catch (error) {
      // Handle different error types
      if (error.response) {
        // Server responded with error
        if (error.response.status === 409) {
          // Conflict - log for manual resolution
          await db.logConflict({
            transactionId: transaction.transactionId,
            conflictType: 'duplicate_or_conflict',
            data: transaction,
            serverResponse: error.response.data
          });
          await db.updateTransactionStatus(transaction.transactionId, 'conflict');
        } else if (error.response.status >= 400 && error.response.status < 500) {
          // Client error - don't retry
          await db.updateTransactionStatus(transaction.transactionId, 'failed', error);
          await db.logConflict({
            transactionId: transaction.transactionId,
            conflictType: 'client_error',
            data: transaction,
            error: error.message
          });
        } else {
          // Server error - retry
          await db.incrementRetryCount(transaction.transactionId);
          await this.delay(this.retryDelay);
        }
      } else {
        // Network error - retry
        await db.incrementRetryCount(transaction.transactionId);
        await this.delay(this.retryDelay);
      }

      throw error;
    }
  }

  /**
   * Get pending transaction count
   */
  async getPendingCount() {
    try {
      const pending = await db.getPendingTransactions();
      return pending.length;
    } catch (error) {
      console.error('Error getting pending count:', error);
      return 0;
    }
  }

  /**
   * Get sync status
   */
  async getSyncStatus() {
    try {
      const stats = await db.getStats();
      return {
        online: this.isOnline,
        syncing: this.syncInProgress,
        pendingTransactions: stats.pendingTransactions,
        unresolvedConflicts: stats.unresolvedConflicts
      };
    } catch (error) {
      console.error('Error getting sync status:', error);
      return null;
    }
  }

  /**
   * Manual sync trigger
   */
  async manualSync() {
    if (!this.isOnline) {
      throw new Error('Cannot sync while offline');
    }
    
    await this.syncPendingTransactions();
  }

  /**
   * Clear all stale transactions (exceeded max retries)
   */
  async clearStaleTransactions() {
    try {
      const cleared = await db.clearStaleTransactions(this.maxRetries);
      console.log(`🗑️ Cleared ${cleared} stale transactions`);
      return cleared;
    } catch (error) {
      console.error('Error clearing stale transactions:', error);
      return 0;
    }
  }

  /**
   * Clear all pending transactions (use with caution!)
   */
  async clearAllPendingTransactions() {
    try {
      const pending = await db.getPendingTransactions();
      for (const txn of pending) {
        await db.deleteTransaction(txn.transactionId);
      }
      console.log(`🗑️ Cleared ${pending.length} pending transactions`);
      return pending.length;
    } catch (error) {
      console.error('Error clearing pending transactions:', error);
      return 0;
    }
  }

  /**
   * Utility: Delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Cache API response
   */
  async cacheResponse(url, data) {
    try {
      await db.cacheApiResponse(url, data);
    } catch (error) {
      console.error('Error caching response:', error);
    }
  }

  /**
   * Get cached response for a URL
   */
  async getCachedResponse(url) {
    try {
      // Extract just the path from the URL
      const urlPath = url.replace(/^https?:\/\/[^\/]+/, '');
      
      // Try to get from cache
      const cached = await db.getCachedData(urlPath);
      
      if (cached && cached.data) {
        return cached.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting cached response:', error);
      return null;
    }
  }

  /**
   * Clear old cache data
   */
  async clearOldCache(daysToKeep = 7) {
    try {
      await db.clearOldCache(daysToKeep);
      console.log(`Cleared cache older than ${daysToKeep} days`);
    } catch (error) {
      console.error('Error clearing old cache:', error);
    }
  }

  /**
   * Debug: Show all pending transactions in console
   */
  async debugShowPendingTransactions() {
    try {
      const pending = await db.getPendingTransactions();
      console.log(`📋 Pending Transactions (${pending.length}):`);
      pending.forEach((txn, i) => {
        console.log(`  ${i + 1}. ${txn.method} ${txn.endpoint}`);
        console.log(`     ID: ${txn.transactionId}`);
        console.log(`     Status: ${txn.status}, Retries: ${txn.retryCount}`);
        console.log(`     Created: ${txn.createdAt}`);
      });
      return pending;
    } catch (error) {
      console.error('Error getting pending transactions:', error);
      return [];
    }
  }

  /**
   * Debug: Show all conflicts (failed syncs)
   */
  async debugShowConflicts() {
    try {
      const conflicts = await db.getUnresolvedConflicts();
      console.log(`⚠️ Unresolved Conflicts (${conflicts.length}):`);
      conflicts.forEach((conflict, i) => {
        console.log(`  ${i + 1}. Type: ${conflict.conflictType}`);
        console.log(`     Transaction ID: ${conflict.transactionId}`);
        console.log(`     Endpoint: ${conflict.data?.endpoint}`);
        console.log(`     Error: ${conflict.error || conflict.serverResponse}`);
        console.log(`     Server Message: ${conflict.serverMessage || 'N/A'}`);
        console.log(`     Status: ${conflict.status}`);
        console.log(`     Created: ${conflict.createdAt}`);
      });
      return conflicts;
    } catch (error) {
      console.error('Error getting conflicts:', error);
      return [];
    }
  }

  /**
   * Clear all conflicts
   */
  async clearAllConflicts() {
    try {
      await db.clearAllConflicts();
      console.log('🗑️ Cleared all conflicts');
    } catch (error) {
      console.error('Error clearing conflicts:', error);
    }
  }

  /**
   * Debug: Show last conflict with full request data
   */
  async debugShowLastConflict() {
    try {
      const conflicts = await db.getUnresolvedConflicts();
      if (conflicts.length === 0) {
        console.log('No conflicts found');
        return null;
      }
      const lastConflict = conflicts[conflicts.length - 1];
      console.log('📋 Last Conflict Details:');
      console.log('  Endpoint:', lastConflict.data?.endpoint);
      console.log('  Method:', lastConflict.data?.method);
      console.log('  Status:', lastConflict.status);
      console.log('  Error:', lastConflict.error);
      console.log('  Server Message:', lastConflict.serverMessage);
      console.log('  Server Response:', lastConflict.serverResponse);
      console.log('  Request Data:', typeof lastConflict.data?.data === 'string' 
        ? JSON.parse(lastConflict.data.data) 
        : lastConflict.data?.data);
      return lastConflict;
    } catch (error) {
      console.error('Error getting last conflict:', error);
      return null;
    }
  }

  /**
   * Delete a specific offline order from local cache
   */
  async deleteOfflineOrder(orderId) {
    try {
      await db.ordersCache.delete(orderId);
      console.log(`🗑️ Deleted offline order: ${orderId}`);
      return true;
    } catch (error) {
      console.error('Error deleting offline order:', error);
      return false;
    }
  }

  /**
   * Debug: Show all offline orders
   */
  async debugShowOfflineOrders() {
    try {
      const orders = await db.getOfflineOrders();
      console.log(`📦 Offline Orders (${orders.length}):`);
      orders.forEach((order, i) => {
        console.log(`  ${i + 1}. ${order.ordName || order.name}`);
        console.log(`     ID: ${order._id}`);
        console.log(`     Status: ${order.ordStatus}`);
        console.log(`     Offline: ${order.offlineOrder}`);
        console.log(`     Created: ${order.createdAt}`);
      });
      return orders;
    } catch (error) {
      console.error('Error getting offline orders:', error);
      return [];
    }
  }
}

// Export singleton instance
const offlineManager = new OfflineManager();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.offlineManager = offlineManager;
}

export default offlineManager;
